import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Plus, Edit, Trash2, Eye, EyeOff, Image, Video, Users } from 'lucide-react';
import { format } from 'date-fns';
import { useStories, Story } from '@/hooks/useStories';
import { StoryEditDialog } from '@/components/StoryEditDialog';

export const StoriesManagementSection = () => {
  const { getAllStories, deleteStory, toggleStoryActive } = useStories();
  const [allStories, setAllStories] = useState<Story[]>([]);
  const [loading, setLoading] = useState(true);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingStory, setEditingStory] = useState<Story | null>(null);

  useEffect(() => {
    const loadAllStories = async () => {
      console.log('Loading all stories for admin...');
      setLoading(true);
      try {
        const storiesData = await getAllStories();
        console.log('Loaded stories data:', storiesData);
        setAllStories(storiesData);
      } catch (error) {
        console.error('Error loading all stories:', error);
      } finally {
        setLoading(false);
      }
    };

    loadAllStories();
  }, []); // Remove getAllStories dependency to prevent infinite loop

  const handleDelete = async (storyId: string) => {
    if (confirm('Վստա՞հ եք, որ ցանկանում եք ջնջել այս պատմությունը:')) {
      try {
        console.log('Admin deleting story:', storyId);
        await deleteStory(storyId);
        
        // Refresh the stories list
        console.log('Refreshing stories after deletion...');
        const updatedStories = await getAllStories();
        console.log('Updated stories after deletion:', updatedStories);
        setAllStories(updatedStories);
      } catch (error) {
        console.error('Error in handleDelete:', error);
        // Even if deletion failed, refresh to get current state
        const updatedStories = await getAllStories();
        setAllStories(updatedStories);
      }
    }
  };

  const handleToggleActive = async (story: Story) => {
    try {
      console.log('Admin toggling story:', story.id, 'current state:', story.is_active);
      await toggleStoryActive(story.id, story.is_active);
      
      // Refresh the stories list
      console.log('Refreshing stories after toggle...');
      const updatedStories = await getAllStories();
      console.log('Updated stories after toggle:', updatedStories);
      setAllStories(updatedStories);
    } catch (error) {
      console.error('Error in handleToggleActive:', error);
      // Refresh to get current state even if toggle failed
      const updatedStories = await getAllStories();
      setAllStories(updatedStories);
    }
  };

  const handleStoryUpdated = async () => {
    // Refresh the stories list to show the updated story
    console.log('Refreshing stories after update...');
    try {
      const updatedStories = await getAllStories();
      console.log('Updated stories after update:', updatedStories);
      setAllStories(updatedStories);
    } catch (error) {
      console.error('Error refreshing stories after update:', error);
    }
  };

  const handleStoryCreated = async () => {
    // Refresh the stories list to show the newly created story
    console.log('Refreshing stories after creation...');
    try {
      const updatedStories = await getAllStories();
      console.log('Updated stories after creation:', updatedStories);
      setAllStories(updatedStories);
    } catch (error) {
      console.error('Error refreshing stories after creation:', error);
    }
  };

  const handleEditStory = (story: Story) => {
    setEditingStory(story);
    setShowEditDialog(true);
  };

  const handleCreateStory = () => {
    setEditingStory(null);
    setShowEditDialog(true);
  };

  const getMediaTypeIcon = (mediaType: string) => {
    return mediaType === 'photo' ? (
      <Image className="h-4 w-4" />
    ) : (
      <Video className="h-4 w-4" />
    );
  };

  const isExpired = (expiresAt: string) => {
    return new Date(expiresAt) < new Date();
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Պատմություններ</CardTitle>
        <CardDescription>
          Կառավարեք օգտատերերի պատմությունները
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Button onClick={handleCreateStory} className="mb-4">
          <Plus className="h-4 w-4 mr-2" />
          Ավելացնել պատմություն
        </Button>
        {loading ? (
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Վերնագիր</TableHead>
                <TableHead>Տեսակ</TableHead>
                <TableHead>Հեղինակ</TableHead>
                <TableHead>Հղում</TableHead>
                <TableHead>Կոճակի տեքստ</TableHead>
                <TableHead>Դիտումներ</TableHead>
                <TableHead>Ստեղծվել է</TableHead>
                <TableHead>Կարգավիճակ</TableHead>
                <TableHead>Գործողություններ</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {allStories.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                    Պատմություններ չկան
                  </TableCell>
                </TableRow>
              ) : (
                allStories.map((story) => (
                  <TableRow key={story.id}>
                    <TableCell className="font-medium">
                      {story.title || 'Անունակ պատմություն'}
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">
                        <div className="flex items-center gap-1">
                          {getMediaTypeIcon(story.media_type)}
                          {story.media_type === 'photo' ? 'Նկար' : 'Վիդեո'}
                        </div>
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {story.profiles 
                        ? `${story.profiles.first_name} ${story.profiles.last_name}`
                        : 'Անհայտ օգտատեր'
                      }
                    </TableCell>
                    <TableCell>
                      {story.link_url ? (
                        <a 
                          href={story.link_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-primary hover:underline text-sm truncate block max-w-32"
                          title={story.link_url}
                        >
                          {story.link_url}
                        </a>
                      ) : (
                        <span className="text-muted-foreground text-sm">Հղում չկա</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {story.button_text ? (
                        <Badge variant="outline" className="text-xs">
                          {story.button_text}
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground text-sm">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Users className="h-4 w-4 text-muted-foreground" />
                        {story.view_count}
                      </div>
                    </TableCell>
                    <TableCell>
                      {format(new Date(story.created_at), 'dd/MM/yyyy HH:mm')}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Badge 
                          variant={
                            !story.is_active 
                              ? "secondary" 
                              : isExpired(story.expires_at)
                                ? "destructive"
                                : "default"
                          }
                        >
                          {!story.is_active 
                            ? 'Անջատված' 
                            : isExpired(story.expires_at)
                              ? 'Ժամկետանց'
                              : 'Ակտիվ'
                          }
                        </Badge>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEditStory(story)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleToggleActive(story)}
                        >
                          {story.is_active ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleDelete(story.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}

        <StoryEditDialog 
          open={showEditDialog} 
          onOpenChange={setShowEditDialog}
          story={editingStory}
          onStoryCreated={handleStoryCreated}
          onStoryUpdated={handleStoryUpdated}
        />
      </CardContent>
    </Card>
  );
};